import React, { useCallback, useEffect, useState } from "react";
import {
  Box, Flex, Text, Button, Badge, Spinner, Center, HStack, VStack, Stack, Select, Input,
  InputGroup, InputLeftElement, Table, Thead, Tbody, Tr, Th, Td, Menu, MenuButton, MenuList,
  MenuItem, IconButton, SimpleGrid, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody,
  ModalFooter, ModalCloseButton, Tooltip,
} from "@chakra-ui/react";
import { Search, MoreVertical, FileText, RefreshCw } from "lucide-react";
import {
  getFilaVendasCanceladas, decidirFilaVendaCancelada, consultarFilaVendaCancelada,
  gerarNotaDevolucaoParaVenda,
} from "../../services/api";
import { formatDateTime } from "../../utils/formatters";
import MarcacoesNotaDevolucao, { marcacoesIniciais, errosMarcacoes, payloadMarcacoes } from "../MarcacoesNotaDevolucao";

const ACCENT = "#F68633";

export const STATUS_FILA = {
  pendente: { label: "Pendente", cor: "orange" },
  pedir_cancelamento: { label: "Pedir cancelamento", cor: "red" },
  aguardando_retorno: { label: "Aguardando retorno", cor: "blue" },
  contador_sipet: { label: "Contador (SIPET)", cor: "purple" },
  devolucao_emitida: { label: "Devolução emitida", cor: "green" },
  resolvida_cancelada: { label: "NF-e cancelada", cor: "gray" },
};

const SITUACAO_SEFAZ = {
  autorizada: { label: "Autorizada", cor: "orange" },
  cancelada: { label: "Cancelada", cor: "gray" },
  denegada: { label: "Denegada", cor: "red" },
  nao_encontrada: { label: "Não encontrada", cor: "red" },
  outra: { label: "Indefinida", cor: "gray" },
};

const selo = (mapa, chave) => {
  const s = mapa[chave] || { label: chave || "-", cor: "gray" };
  return (
    <Badge colorScheme={s.cor} fontSize="10px" px={2} borderRadius="md" whiteSpace="nowrap">{s.label}</Badge>
  );
};

function Horas({ horas, limite }) {
  if (horas === null || horas === undefined) return <Text fontSize="xs">-</Text>;
  const passou = horas > limite;
  return (
    <Tooltip label={passou ? `Passou de ${limite} h: cancelamento só pelo SIPET` : `Faltam ${limite - horas} h para ${limite} h`} hasArrow>
      <Text fontSize="xs" fontWeight="semibold" color={passou ? "red.500" : horas > limite * 0.8 ? "orange.500" : "gray.700"}>
        {horas} h
      </Text>
    </Tooltip>
  );
}

function Acoes({ linha, onAcao, emCurso }) {
  const aberta = !["devolucao_emitida", "resolvida_cancelada"].includes(linha.status);
  return (
    <Menu placement="bottom-end" isLazy>
      <MenuButton as={IconButton} size="sm" variant="ghost" aria-label="Ações"
        icon={<MoreVertical size={16} />} isLoading={emCurso} onClick={(e) => e.stopPropagation()} />
      <MenuList fontSize="sm" zIndex={20}>
        {aberta && linha.sefaz_situacao === "autorizada" && (
          <MenuItem onClick={() => onAcao("pedir_cancelamento")}>Não enviado: pedir cancelamento</MenuItem>
        )}
        {aberta && <MenuItem onClick={() => onAcao("aguardar_retorno")}>Enviado: aguardar retorno</MenuItem>}
        {aberta && <MenuItem onClick={() => onAcao("emitir")}>Emitir nota de devolução</MenuItem>}
        <MenuItem onClick={() => onAcao("consultar")}>Consultar SEFAZ agora</MenuItem>
        {linha.status !== "pendente" && <MenuItem onClick={() => onAcao("reabrir")}>Reabrir</MenuItem>}
      </MenuList>
    </Menu>
  );
}

export default function FilaVendasCanceladas({ toast }) {
  const [lista, setLista] = useState([]);
  const [resumo, setResumo] = useState({});
  const [limite, setLimite] = useState(480);
  const [carregando, setCarregando] = useState(true);
  const [filtros, setFiltros] = useState({ status: "abertos", conta: "", busca: "" });
  const [emCurso, setEmCurso] = useState(null);
  const [emitindo, setEmitindo] = useState(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    const res = await getFilaVendasCanceladas({
      status: filtros.status,
      conta: filtros.conta || undefined,
      busca: filtros.busca || undefined,
    });
    if (res?.error) {
      toast?.({ title: "Não foi possível carregar a fila", description: res.error, status: "error", duration: 6000 });
    }
    setLista(res?.data || []);
    setResumo(res?.resumo || {});
    if (res?.limite_horas) setLimite(res.limite_horas);
    setCarregando(false);
  }, [filtros, toast]);

  useEffect(() => { carregar(); }, [carregar]);

  const atualizarLinha = (nova) => setLista((l) => l.map((x) => (x.id === nova.id ? { ...x, ...nova } : x)));

  const executar = async (linha, acao) => {
    if (acao === "emitir") {
      setEmitindo({ linha, marcacoes: marcacoesIniciais({ tipo: linha.despachada ? "retorno_nao_entregue" : "devolucao" }), mostrarErros: false });
      return;
    }
    setEmCurso(linha.id);
    const res = acao === "consultar"
      ? await consultarFilaVendaCancelada(linha.id)
      : await decidirFilaVendaCancelada(linha.id, acao);
    setEmCurso(null);
    if (res?.error) {
      toast?.({ title: "Não foi possível concluir", description: res.error, status: "error", duration: 6000 });
      return;
    }
    atualizarLinha(res);
    toast?.({
      title: acao === "consultar" ? `SEFAZ: ${(SITUACAO_SEFAZ[res.sefaz_situacao] || {}).label || res.sefaz_situacao}` : "Fila atualizada",
      description: STATUS_FILA[res.status]?.label,
      status: "success",
      duration: 4000,
    });
  };

  const emitir = async () => {
    const erros = errosMarcacoes(emitindo.marcacoes);
    if (Object.keys(erros).length) {
      setEmitindo((e) => ({ ...e, mostrarErros: true }));
      return;
    }
    setEmCurso(emitindo.linha.id);
    const res = await gerarNotaDevolucaoParaVenda(emitindo.linha.venda_id, {
      ...payloadMarcacoes(emitindo.marcacoes),
      fila_id: emitindo.linha.id,
    });
    setEmCurso(null);
    if (res?.error) {
      toast?.({ title: "Não foi possível emitir", description: res.error, status: "error", duration: 7000 });
      return;
    }
    toast?.({
      title: "Nota enfileirada",
      description: `Emissão disparada em ${res?.ambiente === "producao" ? "PRODUÇÃO" : "homologação"}. Acompanhe na aba Notas.`,
      status: "success",
      duration: 6000,
    });
    setEmitindo(null);
    carregar();
  };

  const contagem = (chaves) => chaves.reduce((s, k) => s + (resumo[k] || 0), 0);

  return (
    <Box>
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3} mb={4}>
        {[
          { titulo: "Pendentes", valor: contagem(["pendente"]), cor: "orange.500", status: "pendente" },
          { titulo: "Pedir cancelamento", valor: contagem(["pedir_cancelamento"]), cor: "red.500", status: "pedir_cancelamento" },
          { titulo: "Aguardando retorno", valor: contagem(["aguardando_retorno"]), cor: "blue.500", status: "aguardando_retorno" },
          { titulo: "Contador (SIPET)", valor: contagem(["contador_sipet"]), cor: "purple.500", status: "contador_sipet" },
        ].map((c) => (
          <Box key={c.status} bg="white" borderWidth="1px" borderRadius="lg" p={3} cursor="pointer"
            borderColor={filtros.status === c.status ? ACCENT : "gray.200"} _hover={{ shadow: "sm", borderColor: ACCENT }}
            onClick={() => setFiltros((f) => ({ ...f, status: f.status === c.status ? "abertos" : c.status }))}>
            <Text fontSize="11px" color="gray.500" noOfLines={1}>{c.titulo}</Text>
            <Text fontSize="2xl" fontWeight="bold" color={c.cor} lineHeight="1.2">{c.valor}</Text>
          </Box>
        ))}
      </SimpleGrid>

      <Flex gap={3} mb={4} wrap="wrap">
        <InputGroup size="sm" maxW={{ base: "100%", md: "300px" }}>
          <InputLeftElement pointerEvents="none"><Search size={14} /></InputLeftElement>
          <Input bg="white" placeholder="Pedido, venda, NF-e ou chave" value={filtros.busca}
            onChange={(e) => setFiltros((f) => ({ ...f, busca: e.target.value }))} />
        </InputGroup>
        <Select size="sm" maxW={{ base: "100%", sm: "220px" }} bg="white" value={filtros.status}
          onChange={(e) => setFiltros((f) => ({ ...f, status: e.target.value }))}>
          <option value="abertos">Em aberto</option>
          <option value="todos">Todos</option>
          {Object.entries(STATUS_FILA).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </Select>
        <Select size="sm" maxW={{ base: "100%", sm: "140px" }} bg="white" value={filtros.conta}
          onChange={(e) => setFiltros((f) => ({ ...f, conta: e.target.value }))}>
          <option value="">Todas as contas</option>
          <option value="1">Conta 1</option>
          <option value="2">Conta 2</option>
          <option value="3">Conta 3</option>
        </Select>
        <IconButton size="sm" variant="outline" aria-label="Atualizar" icon={<RefreshCw size={15} />}
          onClick={carregar} isLoading={carregando} />
      </Flex>

      {carregando ? (
        <Center py={16}><Spinner color={ACCENT} /></Center>
      ) : lista.length === 0 ? (
        <Center py={16}>
          <VStack spacing={2}>
            <FileText size={36} color="#CBD5E0" />
            <Text color="gray.500" fontSize="sm">Nenhum pedido cancelado com nota ativa.</Text>
          </VStack>
        </Center>
      ) : (
        <>
          <Stack display={{ base: "flex", md: "none" }} spacing={2}>
            {lista.map((l) => (
              <Box key={l.id} bg="white" borderWidth="1px" borderRadius="lg" p={3}>
                <Flex justify="space-between" align="flex-start" gap={2}>
                  <Box minW={0}>
                    <Text fontSize="sm" fontWeight="bold" color="blue.600" wordBreak="break-all">{l.pedido || `Venda #${l.venda_id}`}</Text>
                    <HStack spacing={2} wrap="wrap" fontSize="xs" color="gray.500">
                      <Text whiteSpace="nowrap">Venda #{l.venda_id}</Text>
                      <Text whiteSpace="nowrap">Conta {l.conta ?? "-"}</Text>
                      <Text whiteSpace="nowrap">NF-e {l.nfe_numero || "-"}</Text>
                    </HStack>
                  </Box>
                  <Acoes linha={l} emCurso={emCurso === l.id} onAcao={(a) => executar(l, a)} />
                </Flex>
                <HStack spacing={3} mt={2} wrap="wrap">
                  {selo(STATUS_FILA, l.status)}
                  {selo(SITUACAO_SEFAZ, l.sefaz_situacao)}
                  <HStack spacing={1}><Text fontSize="xs" color="gray.500">Autorizada há</Text><Horas horas={l.horas_desde_autorizacao} limite={limite} /></HStack>
                  <Text fontSize="xs" color="gray.500">{l.despachada ? "Despachada (CT-e)" : "Sem CT-e"}</Text>
                </HStack>
                {l.nota_devolucao && (
                  <Text fontSize="xs" color="gray.600" mt={1}>
                    Nota de devolução: {l.nota_devolucao.status}{l.nota_devolucao.nfe_numero ? ` (NF-e ${l.nota_devolucao.nfe_numero})` : ""}
                  </Text>
                )}
              </Box>
            ))}
          </Stack>

          <Box display={{ base: "none", md: "block" }} overflowX="auto" borderWidth="1px" borderRadius="lg" bg="white">
            <Table size="sm" variant="simple">
              <Thead bg="gray.50">
                <Tr>
                  <Th>Pedido</Th>
                  <Th>NF-e de venda</Th>
                  <Th>Autorizada há</Th>
                  <Th>Cancelado em</Th>
                  <Th>CT-e</Th>
                  <Th>SEFAZ</Th>
                  <Th>Situação</Th>
                  <Th />
                </Tr>
              </Thead>
              <Tbody>
                {lista.map((l) => (
                  <Tr key={l.id}>
                    <Td minW="170px">
                      <Text fontSize="xs" fontWeight="bold" color="blue.600">{l.pedido || "-"}</Text>
                      <Text fontSize="11px" color="gray.500">Venda #{l.venda_id} · Conta {l.conta ?? "-"}</Text>
                    </Td>
                    <Td fontSize="xs" minW="170px" maxW="200px">
                      <Text>{l.nfe_numero || "-"}</Text>
                      <Text fontSize="10px" color="gray.400" wordBreak="break-all">{l.nfe_chave}</Text>
                    </Td>
                    <Td><Horas horas={l.horas_desde_autorizacao} limite={limite} /></Td>
                    <Td fontSize="xs" color="gray.600">{formatDateTime(l.pedido_cancelado_em, "-")}</Td>
                    <Td fontSize="xs">{l.despachada ? "Sim" : "Não"}</Td>
                    <Td>{selo(SITUACAO_SEFAZ, l.sefaz_situacao)}</Td>
                    <Td>
                      <VStack align="flex-start" spacing={1}>
                        {selo(STATUS_FILA, l.status)}
                        {l.nota_devolucao && (
                          <Text fontSize="10px" color="gray.500">
                            Nota: {l.nota_devolucao.status}{l.nota_devolucao.nfe_numero ? ` ${l.nota_devolucao.nfe_numero}` : ""}
                          </Text>
                        )}
                      </VStack>
                    </Td>
                    <Td textAlign="right"><Acoes linha={l} emCurso={emCurso === l.id} onAcao={(a) => executar(l, a)} /></Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        </>
      )}

      <Modal isOpen={!!emitindo} onClose={() => setEmitindo(null)} size={{ base: "full", sm: "lg" }}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="md">Nota de devolução · pedido {emitindo?.linha?.pedido}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {emitindo && (
              <MarcacoesNotaDevolucao
                valores={emitindo.marcacoes}
                onChange={(m) => setEmitindo((e) => ({ ...e, marcacoes: m }))}
                erros={errosMarcacoes(emitindo.marcacoes)}
                mostrarErros={emitindo.mostrarErros}
              />
            )}
          </ModalBody>
          <ModalFooter gap={2}>
            <Button size="sm" variant="ghost" onClick={() => setEmitindo(null)}>Cancelar</Button>
            <Button size="sm" bg={ACCENT} color="white" _hover={{ bg: "#e07529" }}
              isLoading={emCurso === emitindo?.linha?.id} onClick={emitir}>
              Emitir nota
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
